import { useState } from "preact/hooks";
import { ArrowRightIcon } from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { PricingSection } from "./pricing.tsx";
import { mentatSocialMeta } from "./socialMeta.ts";
import { talkToSkillRoute } from "./talkToSkill.tsx";

export const mentatPrompt =
  `You are Mentat, a personal assistant and second brain powered by prompt2bot.

You communicate with your owner over WhatsApp messages, voice notes, and phone calls.

Core responsibilities:
1. Calendar & Scheduling: Manage Google Calendar events, check availability, book meetings, resolve conflicts, and handle timezones cleanly.
2. Building Dashboards & Automations: You have access to your own cloud computer (terminal, browser, code execution). When the owner needs to track business or life logistics (invoices, leads, projects, daily metrics), build live web apps and dashboards for them. When they need background automation (scraping data, syncing APIs, scheduled reports), write and run the code on your machine.
3. Phone & WhatsApp: Answer calls, conduct morning spoken briefings, and participate in direct or group chats. In group chats, track shared tasks and remind participants.
4. Security & Administrator Approvals: Keep credentials safe. You never leak raw keys. If anyone other than your owner asks you to perform sensitive actions (running code, spinning up VMs, accessing private data), pause and require owner approval via WhatsApp.`;

export const mentatLaunchUrl = `${talkToSkillRoute}?url=${
  encodeURIComponent("tank:@uriva/p2b-personal-assistant")
}&name=Mentat&prompt=${encodeURIComponent(mentatPrompt)}`;

const sectionClass = "py-16 px-4 sm:px-6 max-w-5xl mx-auto w-full";
const cardClass =
  "p-6 sm:p-8 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised transition-all duration-200 shadow-sm";

const PhoneCallIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const CalendarIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const TerminalIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="4 17 10 11 4 5" />
    <line x1="12" y1="19" x2="20" y2="19" />
  </svg>
);

const ShieldIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CheckIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SparklesIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
  </svg>
);

const InteractiveDemo = () => {
  const [activeScenario, setActiveScenario] = useState<
    "dashboard" | "calendar" | "phone" | "security"
  >("dashboard");

  const scenarios = [
    {
      id: "dashboard" as const,
      label: "Live Dashboard",
      promptText:
        "Build me a live dashboard to track our consulting client milestones, revenue, and active invoices.",
      replyText:
        "Spun up an isolated cloud machine. Wrote the database schema, generated the frontend, and deployed your live dashboard link.",
      terminalCmd: "sandbox run node ./generate_dashboard.js",
      terminalOutput: `✓ Provisioning cloud Linux sandbox
✓ SQLite schema initialized: clients, milestones, invoices
✓ Generated React + Vite dashboard
✓ Server running on :8080
✓ Live dashboard deployed`,
    },
    {
      id: "calendar" as const,
      label: "Calendar Shift",
      promptText:
        "Move my 3 PM with David to Thursday at 11 AM and text him a confirmation.",
      replyText:
        "Checked your Thursday schedule. You have an opening from 10:30 AM to 12:00 PM. Moved the Google Calendar event to 11:00 AM and messaged David confirmation.",
      terminalCmd: "gcalendar update --event-id 8a9f --time 'Thu 11:00 AM'",
      terminalOutput: `✓ Fetching calendar events for 2026-09-10
✓ Conflict check: 0 conflicts detected
✓ Event updated: "Sync with David" -> 2026-09-10 11:00:00 UTC
✓ WhatsApp notification dispatched to David`,
    },
    {
      id: "phone" as const,
      label: "Morning Call",
      promptText: "Incoming Audio Call from Mentat (8:00 AM briefing)",
      replyText:
        "Good morning. You have three meetings today starting at 10 AM. The supplier contract arrived on your cloud machine, and the weather in London is 18 degrees.",
      terminalCmd: "voice-agent brief --recipient '+972500000000' --time 08:00",
      terminalOutput: `✓ Daily briefing triggered at 08:00
✓ Calendar agenda compiled: 3 meetings
✓ Vendor documents ingested from cloud workspace
✓ Low-latency WebRTC duplex call initiated
✓ Call completed: duration 1m 42s`,
    },
    {
      id: "security" as const,
      label: "WhatsApp Approval",
      promptText:
        "Team member in group chat: 'Mentat, export all private user emails to a CSV.'",
      replyText:
        "Sensitive action requested by non-owner. Pausing execution. Sending 1-tap approval request to owner WhatsApp.",
      terminalCmd: "security-gate evaluate --action 'export_user_data'",
      terminalOutput: `! Non-owner trigger detected in group chat
! Policy rule matched: SENSITIVE_DATA_EXPORT
! Execution paused at sandbox boundary
✓ 1-tap WhatsApp approval link sent to owner
✓ Waiting for HMAC signature...`,
    },
  ];

  const current = scenarios.find((s) => s.id === activeScenario) ??
    scenarios[0];

  return (
    <div className="w-full my-8 text-left">
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 justify-center">
        {scenarios.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActiveScenario(s.id)}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
              activeScenario === s.id
                ? "bg-accent text-white shadow-md"
                : "bg-surface-raised-light dark:bg-surface-raised text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary border border-border-subtle-light dark:border-border-subtle"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Dual Pane Showcase */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left Pane: WhatsApp Message Interface */}
        <div className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised p-6 flex flex-col justify-between shadow-sm">
          <div>
            <div className="flex items-center justify-between border-b border-border-subtle-light dark:border-border-subtle pb-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-sm">
                  M
                </div>
                <div>
                  <div className="font-semibold text-sm">Mentat Assistant</div>
                  <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
                    Connected via WhatsApp
                  </div>
                </div>
              </div>
              <div className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                Active
              </div>
            </div>

            {/* Conversation bubbles */}
            <div className="space-y-4 text-xs sm:text-sm">
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-accent text-white p-3.5 shadow-sm leading-relaxed">
                  {current.promptText}
                </div>
              </div>

              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-surface-overlay-light dark:bg-surface-overlay text-text-primary-light dark:text-text-primary p-3.5 border border-border-subtle-light dark:border-border-subtle leading-relaxed shadow-sm">
                  {current.replyText}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-3 border-t border-border-subtle-light dark:border-border-subtle text-xs text-text-tertiary-light dark:text-text-tertiary flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span>
              Duplex voice notes, direct messages, and real phone calls
            </span>
          </div>
        </div>

        {/* Right Pane: Isolated Cloud Machine Terminal */}
        <div className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-[#0c1017] p-6 text-white font-mono text-xs flex flex-col justify-between shadow-xl">
          <div>
            <div className="flex items-center justify-between border-b border-gray-800 pb-3 mb-4">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                <span className="text-gray-400 ml-2 text-[11px]">
                  mentat-cloud-sandbox: ~
                </span>
              </div>
              <span className="text-emerald-400 text-[11px] font-sans font-semibold">
                Cloud Linux Isolated
              </span>
            </div>

            <div className="space-y-3 leading-relaxed">
              <div className="text-gray-400">
                ${" "}
                <span className="text-emerald-300">{current.terminalCmd}</span>
              </div>
              <pre className="text-gray-300 whitespace-pre-wrap font-mono text-[11px] sm:text-xs">
                {current.terminalOutput}
              </pre>
            </div>
          </div>

          <div className="mt-6 pt-3 border-t border-gray-800 text-gray-500 text-[11px] font-sans flex items-center justify-between">
            <span>Runs outside your personal laptop</span>
            <span>Zero local files accessed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      q: "How is Mentat different from screen-recording assistants?",
      a: "Screen-recording assistants install software directly on your laptop, watch your display, log keystrokes, and read your active windows. This creates immense privacy and security risk. Mentat stays completely off your personal laptop. It runs on its own isolated Linux cloud machine and communicates with you through phone calls and WhatsApp.",
    },
    {
      q: "Can Mentat really answer and make telephone calls?",
      a: "Yes. Mentat uses low-latency voice pipelines connected to standard telephone lines and WhatsApp audio. It can deliver spoken morning briefings, answer questions while you drive, or call you when an urgent priority occurs.",
    },
    {
      q: "What kind of web dashboards and automations can it build?",
      a: "Mentat has access to a cloud terminal, code execution, and web hosting. When you ask it to track client deliverables, invoices, or metrics, it writes the backend and frontend code, connects a database, and provides you with a private live URL.",
    },
    {
      q: "How does the 1-tap WhatsApp approval system work?",
      a: "If Mentat is added to a group chat and someone other than the owner requests a sensitive action, such as running code or accessing private data, Mentat halts execution immediately. It sends a 1-tap approval link directly to your private WhatsApp, ensuring you retain total control.",
    },
    {
      q: "What access does Mentat need to my Google Calendar?",
      a: "Mentat requests minimal event-level OAuth scope to read and write calendar entries. It never asks for master account passwords or access to your private Google Drive files.",
    },
    {
      q: "Can I customize the system prompt or personality?",
      a: "Yes. The launcher allows you to edit the base system prompt before launching. You can refine its instructions, change its tone, add custom business guidelines, and adjust its tools.",
    },
  ];

  return (
    <section className="py-16 px-4 sm:px-6 max-w-4xl mx-auto w-full text-left">
      <div className="text-center mb-12">
        <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
          Questions & Answers
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
          Frequently Asked Questions
        </h2>
        <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg">
          Clear answers about security, phone integration, and cloud sandboxes.
        </p>
      </div>

      <div className="space-y-3">
        {faqs.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div
              key={idx}
              className="rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                className="w-full p-5 sm:p-6 text-left flex items-center justify-between gap-4 font-semibold text-base sm:text-lg"
              >
                <span>{faq.q}</span>
                <span
                  className={`text-xl font-light transition-transform duration-200 ${
                    isOpen ? "rotate-45 text-accent" : "text-text-secondary"
                  }`}
                >
                  +
                </span>
              </button>
              {isOpen && (
                <div className="px-5 pb-5 sm:px-6 sm:pb-6 text-sm sm:text-base text-text-secondary-light dark:text-text-secondary leading-relaxed border-t border-border-subtle-light/50 dark:border-border-subtle/50 pt-4">
                  {faq.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const TestimonialsSection = () => (
  <section className={sectionClass}>
    <div className="text-center mb-12">
      <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
        Real Delegations
      </div>
      <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
        Trusted by Founders & Operators
      </h2>
      <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
        How operators run their logistics and businesses through Mentat.
      </p>
    </div>

    <div className="grid md:grid-cols-3 gap-6 text-left">
      <div className={cardClass}>
        <div className="text-amber-400 text-sm mb-3">★★★★★</div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-6">
          "Screen-recording assistants were a non-starter because of client
          NDAs. Mentat runs inside its own cloud sandbox, so I can ask it to
          build client dashboards without exposing my laptop."
        </p>
        <div>
          <div className="font-semibold text-sm">Daniel Mercer</div>
          <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
            Principal Consultant, Mercer Strategy
          </div>
        </div>
      </div>

      <div className={cardClass}>
        <div className="text-amber-400 text-sm mb-3">★★★★★</div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-6">
          "Waking up to Mentat's two-minute morning phone call briefing before I
          even open my laptop changes my whole day. It rescheduled two conflicts
          while I was in traffic."
        </p>
        <div>
          <div className="font-semibold text-sm">Sarah Jenkins</div>
          <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
            Founder, Relay Logistics
          </div>
        </div>
      </div>

      <div className={cardClass}>
        <div className="text-amber-400 text-sm mb-3">★★★★★</div>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-6">
          "I added Mentat to our team WhatsApp group. It tracks open
          deliverables, checks them off when someone posts an update, and never
          asks for my personal Google password."
        </p>
        <div>
          <div className="font-semibold text-sm">Kareem Al-Sayed</div>
          <div className="text-xs text-text-tertiary-light dark:text-text-tertiary">
            Head of Product Operations
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const MentatPage = () => {
  usePageMeta({
    title: mentatSocialMeta.title,
    description: mentatSocialMeta.description,
    image: mentatSocialMeta.image,
    twitterCard: "summary_large_image",
  });

  return (
    <div className="flex flex-col w-full text-text-primary-light dark:text-text-primary">
      {/* Hero Section */}
      <section className="pt-12 pb-16 px-4 sm:px-6 max-w-5xl mx-auto w-full text-center">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase mb-6 bg-accent-muted text-accent-light dark:text-accent border border-accent/20 shadow-sm">
          <SparklesIcon className="w-3.5 h-3.5" />
          An External Brain With Its Own Computer
        </div>

        <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6 max-w-4xl mx-auto leading-[1.08]">
          An external cognitive layer with its own{" "}
          <span className="bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-400 bg-clip-text text-transparent">
            phone line, WhatsApp, and computer.
          </span>
        </h1>

        <p className="text-lg sm:text-xl text-text-secondary-light dark:text-text-secondary max-w-3xl mx-auto leading-relaxed mb-10">
          Mentat handles your calendar, answers phone calls, and runs software
          on an isolated cloud machine. It builds live web dashboards and
          automations for your business or daily life without touching your
          personal laptop or watching your screen.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <a
            href={mentatLaunchUrl}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
          >
            <span>Launch Mentat in one click</span>
            <ArrowRightIcon className="w-5 h-5" />
          </a>
        </div>

        {/* Quick Highlights */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto text-left text-xs sm:text-sm text-text-secondary-light dark:text-text-secondary">
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Available today, no waitlist</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Own cloud Linux machine</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Zero desktop screen recording</span>
          </div>
          <div className="flex items-center gap-2 p-3 rounded-xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <CheckIcon className="w-4 h-4 text-emerald-500 shrink-0" />
            <span>Real phone calls & WhatsApp</span>
          </div>
        </div>
      </section>

      {/* Proof Metrics */}
      <section className="py-6 px-4 sm:px-6 max-w-5xl mx-auto w-full">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-left">
          <div className="p-5 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <div className="font-display text-3xl font-bold text-emerald-500 mb-1">
              0%
            </div>
            <div className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
              Desktop Access Required
            </div>
            <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary mt-1">
              No local daemons or screen recording
            </div>
          </div>

          <div className="p-5 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <div className="font-display text-3xl font-bold text-blue-500 mb-1">
              &lt;1s
            </div>
            <div className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
              WhatsApp Response Speed
            </div>
            <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary mt-1">
              Direct voice notes and text messaging
            </div>
          </div>

          <div className="p-5 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <div className="font-display text-3xl font-bold text-purple-500 mb-1">
              100%
            </div>
            <div className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
              Real Duplex Audio Calls
            </div>
            <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary mt-1">
              Standard telephone lines and briefings
            </div>
          </div>

          <div className="p-5 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised">
            <div className="font-display text-3xl font-bold text-amber-500 mb-1">
              1-Tap
            </div>
            <div className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
              WhatsApp Approval Gates
            </div>
            <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary mt-1">
              Hard pause on sensitive actions
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Showcase (The Missing Hero Moment!) */}
      <section
        id="demo"
        className="py-12 px-4 sm:px-6 max-w-5xl mx-auto w-full text-center"
      >
        <div className="text-center mb-8">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent mb-2">
            Live Simulation
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">
            Watch Mentat In Action
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base max-w-2xl mx-auto">
            See how a voice note on WhatsApp triggers execution on an isolated
            cloud computer.
          </p>
        </div>

        <InteractiveDemo />
      </section>

      {/* The Philosophy: Why Mentat */}
      <section
        id="concept"
        className="py-12 px-4 sm:px-6 max-w-4xl mx-auto w-full"
      >
        <div className="p-8 sm:p-10 rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised space-y-4 text-left shadow-sm">
          <div className="text-xs uppercase font-semibold tracking-wider text-accent-light dark:text-accent">
            Why Mentat
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold">
            Most assistants can talk. Very few have hands.
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
            Standard AI assistants like Gemini and ChatGPT are confined to
            browser tabs and chat boxes. They have no computer of their own,
            cannot make real phone calls, cannot join your WhatsApp chats, and
            cannot deploy software. They give advice, but the execution still
            falls entirely on you.
          </p>
          <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
            On the other extreme, desktop agents like Claude Code require
            installing software directly on your laptop, watching your screen,
            or running local terminal sessions. They tie work to your personal
            machine and stop running when you close the lid.
          </p>
          <p className="text-text-secondary-light dark:text-text-secondary leading-relaxed">
            Mentat takes a practical architecture. It operates its own cloud
            Linux machine, builds live dashboards, runs background jobs 24/7,
            and talks to you directly over WhatsApp and real telephone calls.
          </p>
        </div>
      </section>

      {/* Core Capabilities */}
      <section id="capabilities" className={sectionClass}>
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Four pillars of real delegation
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg max-w-2xl mx-auto">
            Practical capability without sacrificing privacy or system security.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Pillar 1: Own Computer */}
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
                <TerminalIcon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">
                1. Its own computer: dashboards & automations
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
                When Mentat needs to do real work, it spins up an isolated cloud
                Linux environment with a terminal, browser, and developer tools.
              </p>
              <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Builds live web dashboards:</strong>{" "}
                    Tell Mentat to track client pipelines, finances, or metrics.
                    It writes the code, connects a database, and gives you a
                    live URL.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Runs background automations:</strong>{" "}
                    Scrape supplier portals, sync records, or compile reports on
                    a schedule.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Leaves your desktop untouched:</strong>{" "}
                    Never watches your screen, tracks cursor movement, or logs
                    your keys.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 2: Phone & WhatsApp */}
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
                <PhoneCallIcon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">
                2. Real phone calls & WhatsApp
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
                You do not need a desktop dashboard open or another app
                cluttering your phone. Mentat lives directly in your normal
                communication channels.
              </p>
              <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Real duplex phone calls:</strong>{" "}
                    Call Mentat from your phone to talk through plans or dictate
                    tasks while driving.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Outbound audio briefings:</strong>{" "}
                    It can call your phone when urgent items surface or for a
                    spoken morning brief.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>WhatsApp direct & group chats:</strong>{" "}
                    Send voice notes and texts. Add Mentat to groups to
                    coordinate tasks and send reminders.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 3: Calendar */}
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
                <CalendarIcon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">
                3. Calendar & scheduling without blanket access
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
                Mentat handles availability, conflict resolution, and booking
                without giving away your digital identity.
              </p>
              <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Availability & booking:</strong>{" "}
                    Ask what your day looks like, reschedule appointments, or
                    find open slots.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Timezone aware:</strong>{" "}
                    Resolves local times and travel offsets cleanly so meetings
                    land on the right hour.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Minimal Google OAuth scope:</strong>{" "}
                    Event-level access only. It never touches your master Google
                    password or private Drive.
                  </span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pillar 4: Security & Approvals */}
          <div className={`${cardClass} flex flex-col justify-between`}>
            <div>
              <div className="w-12 h-12 rounded-xl bg-accent-muted text-accent flex items-center justify-center mb-5">
                <ShieldIcon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold mb-3">
                4. Administrator approvals & secret defense
              </h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed mb-4">
                When an assistant is useful, people in shared groups will try to
                test its boundaries. Mentat is architected for strict safety.
              </p>
              <ul className="space-y-2.5 text-sm text-text-secondary-light dark:text-text-secondary">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>1-tap WhatsApp approvals:</strong>{" "}
                    If anyone other than the owner requests sensitive actions,
                    execution pauses until you approve.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Egress-locked credentials:</strong>{" "}
                    API keys are encrypted and injected at the network proxy.
                    The LLM structurally cannot dump them.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                  <span>
                    <strong>Clean data boundaries:</strong>{" "}
                    No persistent plain-text storage of personal emails or
                    unauthorized retention.
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* A Day With Mentat */}
      <section id="day" className="py-16 px-4 sm:px-6 max-w-4xl mx-auto w-full">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            A day with Mentat
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg">
            How people use it to run their business and schedule.
          </p>
        </div>

        <div className="relative border-l-2 border-border-subtle-light dark:border-border-subtle ml-4 sm:ml-8 pl-6 sm:pl-8 space-y-10 text-left">
          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              8:00 AM · Morning phone call
            </div>
            <h4 className="font-semibold text-base mb-1">
              Audio run-down of your day
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Mentat calls your phone with a quick two-minute audio run-down of
              your meetings, open priorities, and travel weather while you get
              ready.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              10:30 AM · Voice note on WhatsApp
            </div>
            <h4 className="font-semibold text-base mb-1">
              On-demand business dashboard
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              You send a voice note: "Build me a dashboard to track our
              consulting clients, active milestones, and revenue." Mentat opens
              its cloud machine, builds the app, deploys it to a secure link,
              and sends you the live URL.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              1:15 PM · Quick WhatsApp text
            </div>
            <h4 className="font-semibold text-base mb-1">
              Calendar rescheduling
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              You text Mentat between calls: "Move my 3 PM with David to
              Thursday at 11 AM and text him a confirmation." Done in seconds.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              4:00 PM · Automated cloud execution
            </div>
            <h4 className="font-semibold text-base mb-1">
              Vendor metrics & data scrape
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Mentat runs a scheduled script on its cloud computer, logs into
              three vendor portals, extracts pricing changes, and posts a clean
              bulleted brief to your WhatsApp.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full bg-accent border-4 border-surface-light dark:border-surface" />
            <div className="text-xs font-semibold uppercase tracking-wider text-accent-light dark:text-accent mb-1">
              7:30 PM · Family or team group chat
            </div>
            <h4 className="font-semibold text-base mb-1">
              Coordination and task check-off
            </h4>
            <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              In your shared WhatsApp group, Mentat marks finished tasks as
              completed and reminds the group about upcoming plans.
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className={sectionClass}>
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            How it compares
          </h2>
          <p className="text-text-secondary-light dark:text-text-secondary text-base sm:text-lg">
            Architectural difference between browser chatbots, desktop agents,
            and Mentat.
          </p>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised shadow-sm">
          <table className="w-full text-left border-collapse text-sm">
            <thead>
              <tr className="border-b border-border-subtle-light dark:border-border-subtle bg-surface-overlay-light dark:bg-surface-overlay">
                <th className="p-4 sm:p-5 font-semibold">Capability</th>
                <th className="p-4 sm:p-5 font-semibold text-text-secondary-light dark:text-text-secondary">
                  Web AI (Gemini, ChatGPT)
                </th>
                <th className="p-4 sm:p-5 font-semibold text-text-secondary-light dark:text-text-secondary">
                  Desktop Agents (Claude Code)
                </th>
                <th className="p-4 sm:p-5 font-semibold text-accent-light dark:text-accent">
                  Mentat by prompt2bot
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle-light dark:divide-border-subtle">
              <tr>
                <td className="p-4 sm:p-5 font-medium">Where code runs</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  No computer (chat advice only)
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  On your personal laptop
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Dedicated cloud Linux machine
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">WhatsApp integration</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  None (browser tab only)
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  None
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  1:1 chats, voice notes, and shared groups
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">Real telephone calls</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  In-app audio only
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  None
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Real telephone calls over standard lines
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">Software creation</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Text code blocks (you host and run)
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Edits files on your laptop
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Builds, deploys, and hosts live web dashboards
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">
                  Group chat coordination
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  None (single-user chats)
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  None (single-user terminal)
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Tracks deliverables in WhatsApp groups
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">Background execution</td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Stops when you leave the page
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Stops when your laptop sleeps
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  Runs 24/7 scheduled tasks in the cloud
                </td>
              </tr>
              <tr>
                <td className="p-4 sm:p-5 font-medium">
                  Security model
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Provider manages prompts
                </td>
                <td className="p-4 sm:p-5 text-text-secondary-light dark:text-text-secondary">
                  Full access to local machine files
                </td>
                <td className="p-4 sm:p-5 font-semibold text-emerald-600 dark:text-emerald-400">
                  1-tap WhatsApp owner approval links
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Pricing Section */}
      <section className={sectionClass}>
        <PricingSection />
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* FAQ Section */}
      <FaqSection />

      {/* Bottom CTA Banner */}
      <section className="py-20 px-4 sm:px-6 max-w-4xl mx-auto w-full text-center">
        <div className="p-10 sm:p-14 rounded-3xl bg-gradient-to-b from-accent-light/10 via-surface-raised-light to-surface-raised-light dark:from-accent/15 dark:via-surface-raised dark:to-surface-raised border border-accent/20 shadow-xl space-y-6">
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto leading-tight">
            Stop handing over your desktop screen.
          </h2>
          <p className="text-base sm:text-lg text-text-secondary-light dark:text-text-secondary max-w-xl mx-auto leading-relaxed">
            Get an external brain that runs on its own computer, builds the
            dashboards you need, manages your calendar, and answers your calls.
          </p>
          <div className="pt-2 flex items-center justify-center">
            <a
              href={mentatLaunchUrl}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-9 py-4 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
            >
              <span>Launch Mentat in one click</span>
              <ArrowRightIcon className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
